import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronLeft, ChevronRight, Square, Users, Clock, Award, Shield, 
  Wifi, Coffee, Car, X, Play, Loader2, Lock, ShoppingBag, Utensils, 
  ShowerHead, Baby, Accessibility, ParkingCircle, CreditCard, Music,
  Bike, Zap, Heart, Star, MapPin, Phone, Mail, Globe, Camera, Video,
  Mountain, Grid3X3, Dumbbell, Home, Building, Gift, Footprints, Wind,
  Sun, Moon, Thermometer, Headphones, Mic, Tv, Gamepad2, Pizza, Beer, Wrench,
  Puzzle, Ruler, ZoomIn, Rocket, Flame, Gem, PartyPopper, PersonStanding, Trees
} from 'lucide-react';
import axios from 'axios';
import { useTranslations } from '../context/TranslationsContext';

// Map icon string names to Lucide components
const iconMap = {
  Shield, Wifi, Coffee, Car, Lock, ShoppingBag, Utensils, ShowerHead,
  Baby, Accessibility, ParkingCircle, CreditCard, Music, Bike, Zap,
  Heart, Star, MapPin, Phone, Mail, Globe, Camera, Video, Award,
  Users, Clock, Square, Play, ArrowRight, Mountain, Grid3X3, Dumbbell,
  Home, Building, Gift, Footprints, Wind, Sun, Moon, Thermometer,
  Headphones, Mic, Tv, Gamepad2, Pizza, Beer, Wrench, ZoomIn, Rocket, Flame, Gem, PartyPopper, PersonStanding, Trees
};

// Helper to render icon by name
const IconComponent = ({ name, className = "w-5 h-5" }) => {
  const Icon = iconMap[name] || Square;
  return <Icon className={className} />;
};

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback static data
const fallbackSections = [
  {
    id: 'street',
    name: { pl: 'Street Section', en: 'Street Section' },
    description: {
      pl: 'Sekcja street z przeszkodami imitującymi miejskie elementy: schody, poręcze, ławki i krawężniki.',
      en: 'Street section with obstacles imitating urban elements: stairs, rails, benches, and curbs.'
    },
    features: { pl: ['Schody 3-stopniowe', 'Poręcze flat i kink', 'Manuale', 'Krawężniki'], en: ['3-step stairs', 'Flat and kink rails', 'Manual pads', 'Curbs'] },
    image: 'https://images.pexels.com/photos/12580332/pexels-photo-12580332.jpeg?auto=compress&cs=tinysrgb&w=800'
  },
  {
    id: 'bowl',
    name: { pl: 'Bowl & Pool', en: 'Bowl & Pool' },
    description: {
      pl: 'Profesjonalne bowl o różnych głębokościach, idealne do doskonalenia techniki.',
      en: 'Professional bowls of various depths, ideal for perfecting your technique.'
    },
    features: { pl: ['Bowl 1.8m głębokości', 'Mini bowl dla początkujących', 'Coping stalowy', 'Pool corner'], en: ['1.8m deep bowl', 'Mini bowl for beginners', 'Steel coping', 'Pool corner'] },
    image: 'https://images.unsplash.com/photo-1660872761112-67cc2bdee00e?auto=compress&w=800'
  },
  {
    id: 'foam',
    name: { pl: 'Foam Pit', en: 'Foam Pit' },
    description: {
      pl: 'Bezpieczna strefa treningowa z piankowymi materacami do nauki nowych tricków.',
      en: 'Safe training zone with foam mattresses for learning new tricks.'
    },
    features: { pl: ['Foam pit 5x5m', 'Rampa do foam', 'Materace bezpieczeństwa', 'Instruktor na miejscu'], en: ['5x5m foam pit', 'Foam ramp', 'Safety mattresses', 'Instructor on site'] },
    image: 'https://images.unsplash.com/photo-1636715083006-a494358bb99a?auto=compress&w=800'
  }
];

// Stats will use dynamic translations - keys defined here
const statsConfig = [
  { valueKey: 'park.stats.areaValue', labelKey: 'park.stats.area', icon: Ruler, fallbackValue: '3000+', fallbackLabel: { pl: 'm² powierzchni', en: 'sqm of space' } },
  { valueKey: 'park.stats.obstaclesValue', labelKey: 'park.stats.obstacles', icon: Puzzle, fallbackValue: '50+', fallbackLabel: { pl: 'przeszkód', en: 'obstacles' } },
  { valueKey: 'park.stats.daysValue', labelKey: 'park.stats.days', icon: Clock, fallbackValue: '365', fallbackLabel: { pl: 'dni w roku', en: 'days a year' } },
  { valueKey: 'park.stats.ridersValue', labelKey: 'park.stats.riders', icon: Users, fallbackValue: '15k+', fallbackLabel: { pl: 'riderów rocznie', en: 'riders yearly' } }
];

const amenities = [
  { icon: Shield, label: { pl: 'Wypożyczalnia ochraniaczy', en: 'Protective gear rental' } },
  { icon: Coffee, label: { pl: 'Bar & strefa relaksu', en: 'Bar & chill zone' } },
  { icon: Wifi, label: { pl: 'Darmowe WiFi', en: 'Free WiFi' } },
  { icon: Car, label: { pl: 'Parking', en: 'Parking' } },
  { icon: Users, label: { pl: 'Szatnie z prysznicami', en: 'Changing rooms with showers' } },
  { icon: Award, label: { pl: 'Pro shop', en: 'Pro shop' } }
];

const ParkPage = () => {
  const { t, i18n } = useTranslation();
  const { td } = useTranslations();
  const lang = i18n.language?.split('-')[0] || 'pl';
  const [activeSection, setActiveSection] = useState(0);
  
  // Gallery lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  
  // Ref for scrolling to section details
  const sectionDetailsRef = useRef(null);
  
  // Handle section click with scroll
  const handleSectionClick = useCallback((sectionId) => {
    setSelectedSection(sectionId);
    // Scroll to section details after a short delay to allow state update
    setTimeout(() => {
      sectionDetailsRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }, 100);
  }, []);
  
  // Dynamic data from API
  const [parkSections, setParkSections] = useState([]);
  const [obstacles, setObstacles] = useState([]);
  const [dynamicAmenities, setDynamicAmenities] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionsRes, obstaclesRes, amenitiesRes, galleryRes] = await Promise.all([
          axios.get(`${API}/api/park/sections`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/park/obstacles`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/park/amenities`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/park/gallery`).catch(() => ({ data: [] }))
        ]);
        
        const sections = sectionsRes.data.length > 0 ? sectionsRes.data : fallbackSections;
        setParkSections(sections);
        setObstacles(obstaclesRes.data);
        setDynamicAmenities(amenitiesRes.data);
        setGalleryImages(galleryRes.data);
        
        // Select first section by default if available
        if (sections.length > 0) {
          setSelectedSection(sections[0]._id || sections[0].id);
        }
      } catch (error) {
        console.error('Error fetching park data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter obstacles by selected section
  const filteredObstacles = selectedSection 
    ? obstacles.filter(o => o.sectionId === selectedSection)
    : obstacles;
  
  // Get gallery images for selected section + general images
  const sectionGallery = galleryImages.filter(img => 
    img.sectionId === selectedSection || !img.sectionId
  );

  const nextSection = () => setActiveSection((prev) => (prev + 1) % parkSections.length);
  const prevSection = () => setActiveSection((prev) => (prev - 1 + parkSections.length) % parkSections.length);

  // Gallery lightbox functions
  const openLightbox = useCallback((images, startIndex) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  }, [lightboxImages.length]);

  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  }, [lightboxImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage, closeLightbox]);

  // Prepare gallery images array for lightbox
  const getGalleryImagesArray = useCallback(() => {
    const images = galleryImages.length > 0 ? galleryImages : parkSections;
    return images.map(item => {
      const imageUrl = item.url || item.image;
      return imageUrl?.startsWith('/') ? `${API}${imageUrl}` : imageUrl;
    });
  }, [galleryImages, parkSections]);

  // Open gallery lightbox
  const openGalleryLightbox = (index) => {
    const images = getGalleryImagesArray();
    openLightbox(images, index);
  };

  // Open obstacle lightbox
  const openObstacleLightbox = (obstacleImage) => {
    const allObstacleImages = filteredObstacles
      .filter(o => o.image)
      .map(o => o.image.startsWith('/') ? `${API}${o.image}` : o.image);
    
    const index = allObstacleImages.indexOf(
      obstacleImage.startsWith('/') ? `${API}${obstacleImage}` : obstacleImage
    );
    
    openLightbox(allObstacleImages, index >= 0 ? index : 0);
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="park-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] mb-20">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/park-hero.webm"
          poster="/videos/park-hero.jpg"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center px-6"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-['Syne'] font-bold text-white mb-4">
              {td('park.heroTitle', lang === 'pl' ? 'Nasz Park' : 'Our Park')}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              {td('park.heroDescription', lang === 'pl' ? 'Profesjonalny skatepark na światowym poziomie' : 'Professional world-class skatepark')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book" className="btn-primary">
                {td('park.bookSession', lang === 'pl' ? 'Zarezerwuj sesję' : 'Book a session')}
              </Link>
              {/* <button 
                className="btn-secondary flex items-center gap-2"
                onClick={() => openLightbox([parkSections[0]?.image || fallbackSections[0].image], 0)}
              >
                <Play className="w-5 h-5" />
                {td('park.seePark', lang === 'pl' ? 'Zobacz park' : 'See the park')}
              </button> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto mb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {statsConfig.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-6 text-center"
            >
              <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary" />
              <p className="text-3xl md:text-4xl font-bold text-primary mb-1">
                {td(stat.valueKey, stat.fallbackValue)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {td(stat.labelKey, stat.fallbackLabel[lang] || stat.fallbackLabel.pl)}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Description Section */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12"
        >
          <h2 className="text-3xl font-['Syne'] font-bold mb-6 text-gray-900 dark:text-white">
            {td('park.welcome.title', lang === 'pl' ? 'Witaj w AvePark' : 'Welcome to AvePark')}
          </h2>
          <div className="space-y-4">
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              {td('park.welcome.p1', lang === 'pl'
                ? 'Od początku naszej działalności (2009 r.) naszym marzeniem było otwarcie całorocznego krytego i ogrzewanego skateparku. Okazało się jednak, że realizacja tego projektu jest trudniejsza niż zakładaliśmy. Dopiero po przeprowadzce w nowe miejsce w 2015 roku pojawiła się szansa na realizację naszych planów. Nie zamierzaliśmy więc zwlekać!'
                : 'Since the beginning of our activity (2009), our dream has been to open a year-round indoor and heated skate park. However, it turned out that the implementation of this project is more difficult than we expected. It was only after moving to a new location in 2015 that the opportunity to realize our plans appeared. So we did not intend to delay!')}
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              {td('park.welcome.p2', lang === 'pl'
                ? 'Szybka burza mózgów, telefon do TechRamps i już wiedzieliśmy, że jedziemy z tematem! Cel był jeden: nowoczesny, całoroczny, kryty i ogrzewany skatepark. Kilka miesięcy przygotowań, setki maili i spotkań, plany, projekty i długa droga, na końcu której powstało dzieło którego efekty możecie teraz podziwiać. Zapraszamy!'
                : 'A quick brainstorm, a call to TechRamps, and we knew we were on the right track! The goal was clear: a modern, year-round, indoor heated skatepark. Months of preparation, hundreds of emails and meetings, plans, projects, and a long road at the end of which emerged a masterpiece that you can now admire. Welcome!')}
            </p>
          </div>
        </motion.div>
      </section>

      {/* Sections Navigation - Icon Bar */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            {td('park.skatepark', 'SKATEPARK')}
          </span>
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mt-2 text-gray-900 dark:text-white">
            {td('park.sections.title', lang === 'pl' ? 'Sekcje Skateparku' : 'Skatepark Sections')}
          </h2>
        </motion.div>

        {/* Section Icons Navigation */}
        {parkSections.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {parkSections.map((section) => {
              const isActive = selectedSection === (section._id || section.id);
              return (
                <motion.button
                  key={section._id || section.id}
                  onClick={() => handleSectionClick(section._id || section.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-primary/20 text-primary scale-105' 
                      : 'hover:bg-white/10 text-gray-600 dark:text-gray-400 hover:text-primary'
                  }`}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isActive ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-white/10'
                  }`}>
                    <IconComponent name={section.icon || 'Square'} className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-medium text-center max-w-[100px] ${
                    isActive ? 'text-primary' : ''
                  }`}>
                    {section.name?.[lang] || section.name?.pl || 'Sekcja'}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>

      <section ref={sectionDetailsRef} className="px-6 md:px-12 max-w-6xl mx-auto mb-20 text-center scroll-mt-32 pt-4">
      {/* Selected Section Description */}
      {(() => {
        const selectedSectionObj = parkSections.find(s => (s._id || s.id) === selectedSection);
        return selectedSectionObj ? (
          <div className="flex-1 justify-center items-center align-center">
            <img
                        src={selectedSectionObj.image.startsWith('/') ? `${API}${selectedSectionObj.image}` : selectedSectionObj.image}
                        alt={selectedSectionObj.name?.[lang] || selectedSectionObj.name?.pl || 'Section'}
                        className="w-full h-80 object-cover rounded-lg"
                      />

            <h3 className="text-4xl font-semibold mt-12">{selectedSectionObj.name?.[lang] || selectedSectionObj.name?.pl}</h3>
            <p className="text-gray-600 text-md dark:text-gray-300 mt-2 align-center">{selectedSectionObj.description?.[lang] || selectedSectionObj.description?.pl}</p>

          </div>
        ) : null;
      })()}
      </section>

      {/* Obstacles - Zigzag Layout */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto mb-20">
        {filteredObstacles.length > 0 ? (
          <div className="space-y-16">
            {filteredObstacles.map((obstacle, index) => (
              <motion.div
                key={obstacle._id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } gap-8 md:gap-12 items-center`}
              >
                {/* Image */}
                <div className="w-full md:w-1/2">
                  {obstacle.image ? (
                    <div 
                      className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer group"
                      onClick={() => openObstacleLightbox(obstacle.image)}
                    >
                      <img
                        src={obstacle.image.startsWith('/') ? `${API}${obstacle.image}` : obstacle.image}
                        alt={obstacle.name?.[lang] || obstacle.name?.pl || 'Obstacle'}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-[4/3] rounded-2xl bg-gray-200 dark:bg-white/10 flex items-center justify-center">
                      <Mountain className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">
                    {td('park.obstacles.label', lang === 'pl' ? 'PRZESZKODY' : 'OBSTACLES')}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-['Syne'] font-bold mt-2 mb-4 text-gray-900 dark:text-white">
                    {obstacle.name?.[lang] || obstacle.name?.pl || 'Przeszkoda'}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {obstacle.description?.[lang] || obstacle.description?.pl || ''}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            ) : (
              <p>{td('park.noObstacles', lang === 'pl' ? 'Brak przeszkód w tej sekcji' : 'No obstacles in this section')}</p>
            )}
          </div>
        )}
      </section>

      {/* Amenities */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4 text-gray-900 dark:text-white">
            {td('park.amenities.title', lang === 'pl' ? 'Udogodnienia' : 'Amenities')}
          </h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(dynamicAmenities.length > 0 ? dynamicAmenities : amenities).map((amenity, index) => (
            <motion.div
              key={amenity._id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-6 flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                {(() => {
                  // If icon is a string, look it up in iconMap
                  if (typeof amenity.icon === 'string') {
                    const IconComponent = iconMap[amenity.icon];
                    if (IconComponent) {
                      return <IconComponent className="w-6 h-6 text-primary" />;
                    }
                    // Fallback: show first 2 letters if icon not found in map
                    return <span className="text-primary text-xl font-bold">{amenity.icon.slice(0, 2)}</span>;
                  }
                  // If icon is already a component (from static data)
                  if (amenity.icon) {
                    const IconComp = amenity.icon;
                    return <IconComp className="w-6 h-6 text-primary" />;
                  }
                  // Default fallback
                  return <Award className="w-6 h-6 text-primary" />;
                })()}
              </div>
              <p className="font-medium text-gray-900 dark:text-white">
                {amenity.name?.[lang] || amenity.label?.[lang] || amenity.name?.pl || 'N/A'}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4 text-gray-900 dark:text-white">
            {td('park.gallery.title', lang === 'pl' ? 'Galeria' : 'Gallery')}
          </h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(galleryImages.length > 0 ? galleryImages : parkSections).map((item, index) => {
            const imageUrl = item.url || item.image;
            const altText = item.alt?.[lang] || item.name?.[lang] || 'Gallery image';
            return (
              <motion.button
                key={item._id || item.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="aspect-square overflow-hidden rounded-2xl group relative"
                onClick={() => openGalleryLightbox(index)}
              >
                <img
                  src={imageUrl?.startsWith('/') ? `${API}${imageUrl}` : imageUrl}
                  alt={altText}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="px-6 md:px-12 max-w-4xl mx-auto"
      >
        <div className="glass-card p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 dark:text-white">
            {td('park.cta.title', lang === 'pl' ? 'Gotowy na jazdę?' : 'Ready to ride?')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
            {td('park.cta.subtitle', lang === 'pl' 
              ? 'Zarezerwuj swoją sesję online i ciesz się jazdą w najlepszym krytym skateparku w Polsce!'
              : 'Book your session online and enjoy riding at the best indoor skatepark in Poland!')}
          </p>
          <Link
            to="/book"
            className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
            data-testid="park-cta"
          >
            {td('hero.cta')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </motion.div>

      {/* Fullscreen Gallery Lightbox */}
      <AnimatePresence>
        {lightboxOpen && lightboxImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              onClick={closeLightbox}
            >
              <X className="w-8 h-8" />
            </button>

            {/* Image counter */}
            <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-black/50 rounded-full text-white/80 text-sm font-medium">
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>

            {/* Previous button */}
            {lightboxImages.length > 1 && (
              <button
                className="absolute left-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
            )}

            {/* Main image */}
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImages[lightboxIndex]}
                alt={`Gallery ${lightboxIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>

            {/* Next button */}
            {lightboxImages.length > 1 && (
              <button
                className="absolute right-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            )}

            {/* Thumbnail strip */}
            {lightboxImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 px-4 py-3 bg-black/50 backdrop-blur-sm rounded-xl max-w-[90vw] overflow-x-auto">
                {lightboxImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(idx);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                      idx === lightboxIndex 
                        ? 'ring-2 ring-primary scale-105' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParkPage;
